import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { useAuth } from "@context/auth";
import { downloadFile, ServicesURL, uploadFile } from "@utils/utilsApi";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";

const useTIMFunctionality = () => {
  const { t } = useTranslation(["corporate-detail-page"]);
  const { id } = useParams();
  const { user } = useAuth();

  const [openCatalogTIM, setOpenCatalogTIM] = useState(false);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  // funzionalità abilitata per lista corporate indicata in variabile d'ambiente REACT_APP_EXCALIBUR (id separati da ;) es. REACT_APP_EXCALIBUR=1;130,29
  function IsTIM() {
    return process.env.REACT_APP_EXCALIBUR?.split(";").find(
      (enabledId) => enabledId === id,
    );
  }

  const [configData, setConfigData] = useState<
    ActionUploadDownloadIterface | undefined
  >();

  const { trigger: getUploadPresignedRelations } = useSWRMutation(
    `${ServicesURL.getUploadPresignedRelations}`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getUploadPresignedFrequency } = useSWRMutation(
    `${ServicesURL.getUploadPresignedFrequency}`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getDownloadPresignedRelations } = useSWRMutation(
    `${ServicesURL.getDownloadPresignedRelations}`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getDownloadPresignedFrequency } = useSWRMutation(
    `${ServicesURL.getDownloadPresignedFrequency}`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getTemplatePresignedRelations } = useSWRMutation(
    `${ServicesURL.getDownloadPresignedRelations}_template`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getTemplatePresignedFrequency } = useSWRMutation(
    `${ServicesURL.getDownloadPresignedFrequency}_template`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { trigger: getDownloadPresignedNeeds } = useSWRMutation(
    `${ServicesURL.getDownloadPresignedNeeds}`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  //CORPORATE CTA UPLOAD/DOWNLOAD/TEMPLATE ///////////////////////////////////

  const TIMUpdateRelations: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "corporate.catalog",
    permissionUpload: "corporate.catalog",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: `${t("modals-TIM.relations.modal-title")}`,
    label: { en: "", it: "" },
    tooltipFile: false,
    onDownload: async (el: any) => {
      if (el === "template") {
        let response: { url: string; fileId: string } =
          await getTemplatePresignedRelations();
        downloadFile(response.url);
      } else {
        let response: { url: string; fileId: string } =
          await getDownloadPresignedRelations();
        downloadFile(response.url);
      }
    },
    onUpload: async (formData: FormData) => {
      const file: any = formData.get("file");
      const response: { url: string; fileId: string } =
        await getUploadPresignedRelations();
      const fileBlob = new Blob([file], {
        type: file.type,
      });

      return await uploadFile(
        response.url,
        fileBlob,
        MethodType.PUT,
        file.type,
      );
    },
  };

  const TIMCycleLearningObjects: ActionUploadDownloadIterface | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "corporate.catalog",
    permissionUpload: "corporate.catalog",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: `${t("modals-TIM.frequency.modal-title")}`,
    label: { en: "", it: "" },
    tooltipFile: false,
    onDownload: async (el: any) => {
      if (el === "template") {
        let response: { url: string; fileId: string } =
          await getTemplatePresignedFrequency();
        downloadFile(response.url);
      } else {
        let response: { url: string; fileId: string } =
          await getDownloadPresignedFrequency();
        downloadFile(response.url);
      }
    },
    onUpload: async (formData: FormData) => {
      const file: any = formData.get("file");
      const fd = new FormData();
      const fileCsv =
        file.type === "text/csv"
          ? file
          : new File([file], file.name || "frequency.csv", {
              type: "text/csv",
            });

      fd.append("file", fileCsv);

      return await uploadFile(
        `/learning-catalogue/hse/frequency?userId=${user?.id_user}`,
        fd,
        MethodType.POST,
        "multipart/form-data",
      );
    },
  };

  const corporateCatalogActionsCards: {
    key: string;
    title: string;
    icon: string;
    handleClick: () => void;
  }[] = [
    {
      key: "update_relations",
      title: `${t("CTA-download-upload-TIM.relations")}`,
      icon: "upload-download",
      handleClick: () => {
        handleUploadCatalog("update_relations");
        setOpenCatalogTIM(true);
      },
    },
    {
      key: "cycle_los",
      title: `${t("CTA-download-upload-TIM.frequency")}`,
      icon: "upload-download",
      handleClick: () => {
        handleUploadCatalog("cycle_los");
        setOpenCatalogTIM(true);
      },
    },
    {
      key: "needs",
      title: `${t("CTA-download-upload-TIM.needs")}`,
      icon: "download-icon",
      handleClick: async () => {
        let response: { url: string; fileId: string } =
          await getDownloadPresignedNeeds();
        downloadFile(response.url);
        setOpenToast({ s: StatusEnum.OK, message: "" });
      },
    },
  ];

  const handleUploadCatalog = (action_key: string) => {
    switch (action_key) {
      case "update_relations":
        setConfigData(TIMUpdateRelations);
        break;
      case "cycle_los":
        setConfigData(TIMCycleLearningObjects);
        break;
      default:
        break;
    }
  };

  /////////////////////////////////////////////////////////////////////////////

  return {
    corporateCatalogActionsCards,
    configData,
    setConfigData,
    IsTIM,
    openCatalogTIM,
    setOpenCatalogTIM,
    setOpenToast,
    openToast,
  };
};

export default useTIMFunctionality;
